import React, { useRef, useEffect } from 'react';

interface AudioVisualizerProps {
  analyserNode: AnalyserNode | null;
  isRecording: boolean;
  volumeLevel: number;
  moodColor?: string;
  height?: number;
}

export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  analyserNode,
  isRecording,
  volumeLevel: _volumeLevel,
  moodColor: _moodColor = '#8B5CF6',
  height: propHeight = 52
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animFrameRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    let currentWidth = canvas.parentElement?.clientWidth || 320;
    const height = propHeight;

    const resizeCanvas = () => {
      if (!canvas || !canvas.parentElement) return;
      currentWidth = Math.max(120, canvas.parentElement.clientWidth);
      canvas.width = Math.floor(currentWidth * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(1, 0, 0, 1, 0, 0); // reset transform
      ctx.scale(dpr, dpr);
    };

    resizeCanvas();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && canvas.parentElement) {
      resizeObserver = new ResizeObserver(() => {
        resizeCanvas();
      });
      resizeObserver.observe(canvas.parentElement);
    }

    // Allocate buffers once
    const fftSize = analyserNode ? analyserNode.fftSize : 256;
    const binCount = analyserNode ? analyserNode.frequencyBinCount : 128;
    const freqData = new Uint8Array(binCount);
    const timeData = new Uint8Array(fftSize);

    const render = () => {
      const width = currentWidth;
      ctx.clearRect(0, 0, width, height);

      // Check for suspended context
      if (analyserNode && analyserNode.context && (analyserNode.context as AudioContext).state === 'suspended') {
        if (typeof (analyserNode.context as AudioContext).resume === 'function') {
          (analyserNode.context as AudioContext).resume().catch(() => {});
        }
      }

      if (!analyserNode) {
        // Idle resting baseline wave maintaining vibrant studio violet
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(139, 92, 246, 0.45)';
        ctx.lineWidth = 1.5;
        const time = Date.now() * 0.002;
        for (let x = 0; x < width; x += 4) {
          const y = height / 2 + Math.sin(x * 0.03 + time) * 2;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
        animFrameRef.current = requestAnimationFrame(render);
        return;
      }

      // Fetch live frequency and waveform data
      analyserNode.getByteFrequencyData(freqData);
      analyserNode.getByteTimeDomainData(timeData);

      // Measure active energy & peak deviation
      let sum = 0;
      for (let i = 0; i < binCount; i++) {
        sum += freqData[i];
      }
      const avgFreq = sum / binCount;

      let maxDiff = 0;
      for (let i = 0; i < fftSize; i++) {
        const diff = Math.abs(timeData[i] - 128);
        if (diff > maxDiff) maxDiff = diff;
      }

      const hasAudio = avgFreq > 2 || maxDiff > 3;

      // Draw background frequency response bars
      const barCount = Math.min(48, Math.max(24, Math.floor(width / 12)));
      const barSpacing = 2.5;
      const totalSpacing = (barCount - 1) * barSpacing;
      const barWidth = Math.max(2, (width - totalSpacing) / barCount);
      const step = Math.max(1, Math.floor(binCount / barCount));

      for (let i = 0; i < barCount; i++) {
        const rawValue = freqData[i * step] || 0;
        // Resting floor keeps subtle breathing life
        const restingFloor = 2.5 + Math.sin(i * 0.35 + Date.now() * 0.0025) * 1.5;
        const scale = (rawValue / 255);
        const value = Math.max(restingFloor, scale * (height - 8));
        const barHeight = Math.min(height - 4, Math.max(2, value));
        const x = i * (barWidth + barSpacing);
        const y = height - barHeight;

        // Dynamic gradients: Maintain full vibrant color palette at all times (no graying out on silence!)
        const gradient = ctx.createLinearGradient(0, height, 0, y);
        if (isRecording) {
          gradient.addColorStop(0, 'rgba(239, 68, 68, 0.25)');
          gradient.addColorStop(0.5, '#ef4444');
          gradient.addColorStop(1, '#fca5a5');
        } else {
          // Vibrant Neon Violet / Sky Cyan maintained continuously
          gradient.addColorStop(0, 'rgba(139, 92, 246, 0.25)');
          gradient.addColorStop(0.5, '#8b5cf6');
          gradient.addColorStop(1, '#38bdf8');
        }

        ctx.fillStyle = gradient;
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(x, y, barWidth, barHeight, [2, 2, 0, 0]);
        } else {
          ctx.rect(x, y, barWidth, barHeight);
        }
        ctx.fill();
      }

      // Draw real-time center oscilloscope waveform trace (maintaining vivid neon cyan/blue on silence)
      ctx.save();
      ctx.beginPath();
      ctx.lineWidth = isRecording ? 2.2 : (hasAudio ? 2.0 : 1.75);
      ctx.strokeStyle = isRecording
        ? 'rgba(254, 202, 202, 0.95)'
        : 'rgba(56, 189, 248, 0.95)';

      if (isRecording) {
        ctx.shadowColor = 'rgba(239, 68, 68, 0.85)';
        ctx.shadowBlur = 8;
      } else {
        ctx.shadowColor = 'rgba(56, 189, 248, 0.75)';
        ctx.shadowBlur = hasAudio ? 6 : 4;
      }

      const sliceWidth = width / (fftSize - 1);
      let xPos = 0;
      for (let i = 0; i < fftSize; i++) {
        const v = (timeData[i] - 128) / 128.0;
        const restingIdle = !hasAudio ? Math.sin(i * 0.08 + Date.now() * 0.003) * 1.5 : 0;
        const yPos = (height / 2) + (v * (height / 2 - 4)) + restingIdle;

        if (i === 0) ctx.moveTo(xPos, yPos);
        else ctx.lineTo(xPos, yPos);
        xPos += sliceWidth;
      }
      ctx.stroke();
      ctx.restore();

      animFrameRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [analyserNode, isRecording, propHeight]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: `${propHeight}px`,
        overflow: 'hidden'
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: `${propHeight}px`,
          display: 'block',
          background: 'transparent'
        }}
      />
    </div>
  );
};
